import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, Download, MessageCircle, Search, Check, X, Share2, Mail } from "lucide-react";
import { Button } from "./ui/button";
import { openAuthenticatedPdf, orderPdfFilename } from "../utils/pdf";
import { canShareFiles, shareFileNative } from "../utils/share";
import RejectOrderDialog from "./RejectOrderDialog";
import SendDocEmailDialog from "./SendDocEmailDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SHARE_SUPPORTED = canShareFiles();

/**
 * Unified row-level actions for any order list (Admin, Agent, Warehouse, Customer).
 *
 * Props:
 *   order              required. The order object (must have id + order_number)
 *   forCustomer        bool - if true, PDF is fetched with for_customer=true (hides margins/costs)
 *   onOpenDetail       fn(order) - opens the detail modal
 *   onOpenChat         fn(orderId) - opens the chat drawer scoped to this order
 *   onApproved         fn() - called after admin/agent approves a pending_agent order
 *   showApprove        bool - shows Approve/Reject buttons for pending_agent orders (admin/agent only)
 *   size               'sm' (default) | 'md'
 *   showLabels         bool - shows text labels on desktop (default false: icons only)
 */
const OrderRowActions = ({ order, forCustomer = false, onOpenDetail, onOpenChat, onApproved, showApprove = false, size = "sm", showLabels = false, emailEnabled = false }) => {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [mailOpen, setMailOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const previewPdf = (e) => {
    e?.stopPropagation?.();
    const qs = forCustomer ? "?for_customer=true&inline=true" : "?inline=true";
    openAuthenticatedPdf(`/orders/${order.id}/pdf${qs}`);
  };

  const downloadPdf = (e) => {
    e?.stopPropagation?.();
    const qs = forCustomer ? "?for_customer=true" : "";
    openAuthenticatedPdf(`/orders/${order.id}/pdf${qs}`, { download: true, filename: orderPdfFilename(order, { forCustomer }) });
  };

  const sharePdf = async (e) => {
    e?.stopPropagation?.();
    if (sharing) return;
    setSharing(true);
    try {
      const qs = forCustomer ? "?for_customer=true" : "";
      const r = await axios.get(`${API}/orders/${order.id}/pdf${qs}`, { responseType: "blob" });
      const filename = orderPdfFilename(order, { forCustomer });
      await shareFileNative({ blob: r.data, filename, title: filename });
    } catch {
      toast.error("Errore nella condivisione del PDF");
    } finally {
      setSharing(false);
    }
  };

  const openChat = (e) => {
    e?.stopPropagation?.();
    if (onOpenChat) onOpenChat(order.id);
  };

  const openDetail = (e) => {
    e?.stopPropagation?.();
    if (onOpenDetail) onOpenDetail(order);
  };

  const btnH = size === "md" ? "h-9" : "h-8";

  const approve = async (e) => {
    e?.stopPropagation?.();
    if (!window.confirm(`Approvare ordine ${order.order_number}?`)) return;
    try {
      await axios.post(`${API}/orders/${order.id}/agent-approve`);
      toast.success("Ordine approvato");
      onApproved && onApproved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore approvazione");
    }
  };

  const reject = (e) => {
    e?.stopPropagation?.();
    setRejectOpen(true);
  };

  const showApproveBtns = showApprove && order.status === "pending_agent";

  return (
    <div className="inline-flex items-center gap-1 flex-wrap" data-testid={`order-actions-${order.order_number}`}>
      {showApproveBtns && (
        <>
          <Button size="sm" onClick={approve} className={`bg-green-600 hover:bg-green-700 text-white ${btnH} px-2`} title="Approva" data-testid={`order-approve-${order.order_number}`}>
            <Check className="w-3.5 h-3.5" />{showLabels && <span className="ml-1 hidden lg:inline">Approva</span>}
          </Button>
          <Button size="sm" variant="outline" onClick={reject} className={`border-red-300 text-red-600 hover:bg-red-50 ${btnH} px-2`} title="Rifiuta" data-testid={`order-reject-${order.order_number}`}>
            <X className="w-3.5 h-3.5" />{showLabels && <span className="ml-1 hidden lg:inline">Rifiuta</span>}
          </Button>
        </>
      )}
      <Button
        size="sm"
        variant="outline"
        onClick={previewPdf}
        className={`border-stone-300 ${btnH} px-2`}
        title="Anteprima PDF"
        data-testid={`order-preview-${order.order_number}`}
      >
        <Eye className="w-3.5 h-3.5" />{showLabels && <span className="ml-1 hidden lg:inline">Anteprima</span>}
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={downloadPdf}
        className={`border-stone-300 ${btnH} px-2`}
        title="Scarica PDF"
        data-testid={`order-download-${order.order_number}`}
      >
        <Download className="w-3.5 h-3.5" />{showLabels && <span className="ml-1 hidden lg:inline">PDF</span>}
      </Button>
      {SHARE_SUPPORTED && (
        <Button
          size="sm"
          variant="outline"
          onClick={sharePdf}
          disabled={sharing}
          className={`border-emerald-300 text-emerald-700 hover:bg-emerald-50 ${btnH} px-2`}
          title="Condividi PDF (WhatsApp, Email, AirDrop...)"
          data-testid={`order-share-${order.order_number}`}
        >
          <Share2 className="w-3.5 h-3.5" />{showLabels && <span className="ml-1 hidden lg:inline">Condividi</span>}
        </Button>
      )}
      {emailEnabled && (
        <Button
          size="sm"
          variant="outline"
          onClick={(e) => { e?.stopPropagation?.(); setMailOpen(true); }}
          className={`border-[#8A1C2A]/40 text-[#8A1C2A] hover:bg-[#8A1C2A]/5 ${btnH} px-2`}
          title="Invia via email dalla tua casella"
          data-testid={`order-email-${order.order_number}`}
        >
          <Mail className="w-3.5 h-3.5" />{showLabels && <span className="ml-1 hidden lg:inline">Email</span>}
        </Button>
      )}
      {onOpenChat && (
        <Button
          size="sm"
          variant="outline"
          onClick={openChat}
          className={`border-[#C5A059] text-[#8A1C2A] hover:bg-[#C5A059]/10 ${btnH} px-2`}
          title="Chat ordine"
          data-testid={`order-chat-${order.order_number}`}
        >
          <MessageCircle className="w-3.5 h-3.5" />{showLabels && <span className="ml-1 hidden lg:inline">Chat</span>}
        </Button>
      )}
      {onOpenDetail && (
        <Button
          size="sm"
          onClick={openDetail}
          className={`bg-[#8A1C2A] hover:bg-[#6B1420] text-white ${btnH} px-2`}
          title="Apri dettaglio"
          data-testid={`order-detail-${order.order_number}`}
        >
          <Search className="w-3.5 h-3.5" />{showLabels && <span className="ml-1 hidden lg:inline">Dettaglio</span>}
        </Button>
      )}
      <RejectOrderDialog open={rejectOpen} onClose={() => setRejectOpen(false)} order={order} onRejected={onApproved} />
      {mailOpen && (
        <SendDocEmailDialog
          open={mailOpen}
          onClose={() => setMailOpen(false)}
          title={`Invia ordine ${order.order_number}`}
          attachmentLabel={`Conferma ordine ${order.order_number} (PDF)`}
          endpoint={`${API}/docs/send-email`}
          extraPayload={{ doc_refs: [{ type: "order", id: order.id }] }}
          subjectDefault={`Ordine ${order.order_number} — Giotto Carni`}
          bodyDefault={`Buongiorno,\n\nin allegato la conferma dell'ordine ${order.order_number}.\nRestiamo a disposizione per qualsiasi chiarimento.\n\nCordiali saluti`}
          presetCustomerId={order.customer_id}
        />
      )}
    </div>
  );
};

export default OrderRowActions;
